import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { StatCard } from './common/StatCard';
import { EmptyState } from './common/States';
import { equityAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { Coins, Plus, Eye, Trash2, Loader2, TrendingUp, Users, Lock, CircleDollarSign, Ban } from 'lucide-react';
import Swal from 'sweetalert2';

const GRANT_TYPES = ['RSU', 'ESOP', 'ISO', 'NSO', 'SAR', 'PHANTOM'];
const FREQUENCIES = ['monthly', 'quarterly', 'semiannual', 'annual'];
const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.employees || d?.items || d?.results || [];
};

const EMPTY = {
  employee_id: '', grant_number: '', grant_type: 'RSU', grant_date: '', quantity: '',
  strike_price: '', currency: 'INR', vesting_start_date: '', cliff_months: 12,
  total_months: 48, vesting_frequency: 'monthly', notes: '',
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-600', fully_vested: 'bg-indigo-600',
    terminated: 'bg-amber-600', cancelled: '',
  };
  return <Badge className={map[status] || ''} variant={status === 'cancelled' ? 'secondary' : 'default'}>{status.replace('_', ' ')}</Badge>;
};

const VestingSchedule = ({ schedule }) => (
  <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-md border">
    <Table>
      <TableHeader className="sticky top-0 bg-background">
        <TableRow><TableHead>Vest date</TableHead><TableHead>Units</TableHead><TableHead>Cumulative</TableHead><TableHead>State</TableHead></TableRow>
      </TableHeader>
      <TableBody>
        {schedule.map((s, i) => (
          <TableRow key={i} className={s.cancelled ? 'opacity-50' : ''}>
            <TableCell className="whitespace-nowrap">{s.vest_date}{s.is_cliff && <Badge variant="outline" className="ml-2">cliff</Badge>}</TableCell>
            <TableCell>{fmt(s.quantity)}</TableCell>
            <TableCell>{fmt(s.cumulative)}</TableCell>
            <TableCell>
              {s.cancelled ? <Badge variant="secondary">forfeited</Badge>
                : s.vested ? <Badge className="bg-emerald-600">vested</Badge>
                  : <Badge variant="outline">upcoming</Badge>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const GrantSummaryCards = ({ summary, currency }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    <StatCard label="Granted" value={fmt(summary.total)} icon={CircleDollarSign} />
    <StatCard label="Vested" value={fmt(summary.vested)} icon={TrendingUp} />
    <StatCard label="Unvested" value={fmt(summary.unvested)} icon={Lock} />
    <StatCard label="% Vested" value={`${summary.percent_vested ?? 0}%`} icon={Coins} />
  </div>
);

const EquityManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [grants, setGrants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([equityAPI.listGrants(), equityAPI.summary().catch(() => ({ data: null }))]);
      setGrants(toArray(g));
      setSummary(s.data);
    } catch (err) {
      if (err?.response?.status === 402 || err?.response?.status === 403) setGrants([]);
      else Swal.fire('Could not load equity', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (canManage) employeeAPI.list().then((r) => setEmployees(toArray(r))).catch(() => setEmployees([]));
  }, [canManage]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.employee_id) return Swal.fire('Employee required', 'Select the grantee.', 'warning');
    if (!form.grant_date) return Swal.fire('Grant date required', 'Pick a grant date.', 'warning');
    if (!(Number(form.quantity) > 0)) return Swal.fire('Quantity required', 'Enter a positive number of units.', 'warning');
    setSaving(true);
    try {
      await equityAPI.createGrant({
        employee_id: Number(form.employee_id),
        grant_number: form.grant_number || undefined,
        grant_type: form.grant_type,
        grant_date: form.grant_date,
        quantity: Number(form.quantity),
        strike_price: form.strike_price === '' ? null : Number(form.strike_price),
        currency: form.currency || 'INR',
        vesting_start_date: form.vesting_start_date || form.grant_date,
        cliff_months: Number(form.cliff_months) || 0,
        total_months: Number(form.total_months) || 0,
        vesting_frequency: form.vesting_frequency,
        notes: form.notes || null,
      });
      setDialogOpen(false); setForm(EMPTY);
      await load();
      Swal.fire({ icon: 'success', title: 'Grant created', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (g) => {
    try { setDetail((await equityAPI.getGrant(g.id)).data); }
    catch (err) { Swal.fire('Could not load grant', err?.response?.data?.detail || '', 'error'); }
  };

  const terminate = async (g) => {
    const { value: d } = await Swal.fire({
      title: 'Terminate grant', input: 'date',
      inputLabel: 'Termination date (unvested tranches after this are forfeited)',
      showCancelButton: true, confirmButtonText: 'Terminate', confirmButtonColor: '#b5456b',
    });
    if (!d) return;
    try { await equityAPI.terminateGrant(g.id, d); await load(); if (detail?.id === g.id) openDetail(g); Swal.fire({ icon: 'success', title: 'Grant terminated', timer: 1200, showConfirmButton: false }); }
    catch (err) { Swal.fire('Failed', err?.response?.data?.detail || '', 'error'); }
  };

  const remove = async (g) => {
    const ok = await Swal.fire({ icon: 'warning', title: 'Delete grant?', text: g.grant_number, showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b' });
    if (!ok.isConfirmed) return;
    try { await equityAPI.deleteGrant(g.id); await load(); }
    catch (err) { Swal.fire('Delete failed', err?.response?.data?.detail || '', 'error'); }
  };

  const totals = summary?.totals;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Coins className="h-6 w-6 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-semibold">Equity &amp; Vesting</h2>
            <p className="text-sm text-muted-foreground">ESOP / RSU grants and vesting schedules.</p>
          </div>
        </div>
        {canManage && <Button onClick={() => { setForm(EMPTY); setDialogOpen(true); }}><Plus className="h-4 w-4 mr-1" /> New Grant</Button>}
      </div>

      {totals && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Granted" value={fmt(totals.granted)} icon={CircleDollarSign} />
          <StatCard label="Vested" value={fmt(totals.vested)} icon={TrendingUp} />
          <StatCard label="Unvested" value={fmt(totals.unvested)} icon={Lock} />
          <StatCard label="Holders" value={fmt(totals.holders)} icon={Users} />
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
          ) : grants.length === 0 ? (
            <EmptyState icon={Coins} title="No equity grants yet" description={canManage ? 'Create a grant to start tracking vesting.' : 'No grants have been recorded.'} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grant #</TableHead><TableHead>Employee</TableHead><TableHead>Type</TableHead>
                    <TableHead>Quantity</TableHead><TableHead>% Vested</TableHead><TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell className="font-medium">{g.grant_number}</TableCell>
                      <TableCell>{g.employee_name || g.employee_code || '—'}</TableCell>
                      <TableCell><Badge variant="secondary">{g.grant_type}</Badge></TableCell>
                      <TableCell>{fmt(g.quantity)}</TableCell>
                      <TableCell>{g.summary?.percent_vested ?? 0}%</TableCell>
                      <TableCell><StatusBadge status={g.status} /></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => openDetail(g)} title="View"><Eye className="h-4 w-4" /></Button>
                        {canManage && g.status !== 'terminated' && g.status !== 'cancelled' && (
                          <Button variant="ghost" size="icon" onClick={() => terminate(g)} title="Terminate"><Ban className="h-4 w-4 text-amber-600" /></Button>
                        )}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(g)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New equity grant</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => set('employee_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={String(e.id)}>{[e.first_name, e.last_name].filter(Boolean).join(' ')} · {e.employee_id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Grant number</Label><Input value={form.grant_number} onChange={(e) => set('grant_number', e.target.value)} placeholder="auto if blank" /></div>
            <div className="space-y-1"><Label>Type</Label>
              <Select value={form.grant_type} onValueChange={(v) => set('grant_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{GRANT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Grant date *</Label><Input type="date" value={form.grant_date} onChange={(e) => set('grant_date', e.target.value)} /></div>
            <div className="space-y-1"><Label>Quantity (units) *</Label><Input type="number" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} placeholder="4800" /></div>
            <div className="space-y-1"><Label>Strike price</Label><Input type="number" value={form.strike_price} onChange={(e) => set('strike_price', e.target.value)} placeholder="options only" /></div>
            <div className="space-y-1"><Label>Currency</Label><Input value={form.currency} onChange={(e) => set('currency', e.target.value)} /></div>
            <div className="space-y-1"><Label>Vesting start</Label><Input type="date" value={form.vesting_start_date} onChange={(e) => set('vesting_start_date', e.target.value)} placeholder="= grant date if blank" /></div>
            <div className="space-y-1"><Label>Frequency</Label>
              <Select value={form.vesting_frequency} onValueChange={(v) => set('vesting_frequency', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Cliff (months)</Label><Input type="number" value={form.cliff_months} onChange={(e) => set('cliff_months', e.target.value)} /></div>
            <div className="space-y-1"><Label>Total vesting (months)</Label><Input type="number" value={form.total_months} onChange={(e) => set('total_months', e.target.value)} /></div>
            <div className="space-y-1 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create grant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.grant_number} · {detail.employee_name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  {detail.grant_type} · {fmt(detail.quantity)} units · granted {detail.grant_date} ·
                  {' '}{detail.cliff_months}mo cliff / {detail.total_months}mo {detail.vesting_frequency}
                  {detail.strike_price ? ` · strike ${detail.currency} ${detail.strike_price}` : ''}
                </div>
                <GrantSummaryCards summary={detail.summary} currency={detail.currency} />
                {detail.summary?.next_vest_date && (
                  <div className="text-sm">Next vest: <b>{fmt(detail.summary.next_vest_quantity)}</b> units on <b>{detail.summary.next_vest_date}</b></div>
                )}
                <VestingSchedule schedule={detail.schedule || []} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Employee self-service view.
export const MyEquity = () => {
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  useEffect(() => {
    equityAPI.myEquity()
      .then((r) => setGrants(toArray(r)))
      .catch(() => setGrants([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  if (!grants.length) return <EmptyState icon={Coins} title="No equity grants" description="You don't have any equity grants recorded yet." />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Coins className="h-6 w-6 text-indigo-600" />
        <div><h2 className="text-2xl font-semibold">My Equity</h2><p className="text-sm text-muted-foreground">Your grants and vesting progress.</p></div>
      </div>
      {grants.map((g) => (
        <Card key={g.id}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">{g.grant_number} <Badge variant="secondary">{g.grant_type}</Badge> <StatusBadge status={g.status} /></CardTitle>
            <CardDescription>{fmt(g.quantity)} units · granted {g.grant_date} · {g.cliff_months}mo cliff / {g.total_months}mo {g.vesting_frequency}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <GrantSummaryCards summary={g.summary} currency={g.currency} />
            {g.summary?.next_vest_date && <div className="text-sm">Next vest: <b>{fmt(g.summary.next_vest_quantity)}</b> units on <b>{g.summary.next_vest_date}</b></div>}
            <VestingSchedule schedule={g.schedule || []} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default EquityManagement;
